import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import Badge from './Shared/Badge';
import { logError } from '../utils/logger';
import { computeAvailableSlots as computeSlots } from '../utils/slots';
import { today } from '../utils/formatters';
import { useBookingsRealtime } from '../hooks/useBookingsRealtime';
import {
  fetchBookings as apiFetchBookings, fetchBookingPhotos,
  insertManualBooking, updateBooking, deleteBooking as apiDeleteBooking,
  archiveBooking as apiArchiveBooking,
  fetchShopDataForBookings, fetchSubServices, setCalendarEventId,
} from '../services/bookingService';
import { fetchCategories } from '../services/serviceBuilderService';
import { incrementClientNoShow, upsertClientFromBooking } from '../services/clientService';
import { sendEmail, invokeCalendar, processRefund } from '../services/edgeFunctionService';

export default function Bookings() {
  const [bks,              setBks]              = useState([]);
  const [sendingId,        setSendingId]        = useState(null);
  const [editingBooking,   setEditingBooking]   = useState(null);
  const [editForm,         setEditForm]         = useState({ booking_date: '', booking_time: '', service_name: '', client_name: '', client_phone: '', client_email: '', total_amount: '', deposit_amount: '', status: '' });
  const [shopHours,        setShopHours]        = useState([]);
  const [holidays,         setHolidays]         = useState([]);
  const [lunchBreak,       setLunchBreak]       = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const [alertFired,       setAlertFired]       = useState(new Set());
  const [notifStatus,      setNotifStatus]      = useState('default');
  const [photosByBooking,  setPhotosByBooking]  = useState({});
  const [viewingPhotos,    setViewingPhotos]    = useState(null);
  const [refundingId,      setRefundingId]      = useState(null);
  const [refundAmount,     setRefundAmount]     = useState('');
  const [refundLoading,    setRefundLoading]    = useState(false);
  const [completingBooking, setCompletingBooking] = useState(null);
  const [paymentDetails,   setPaymentDetails]   = useState({ card: '', cash: '', transfer: '' });
  const [addingManual,      setAddingManual]      = useState(false);
  const [allServices,       setAllServices]       = useState([]);
  const [allCategories,     setAllCategories]     = useState([]);
  const [manualSelections,  setManualSelections]  = useState({});
  const [filterStatus,      setFilterStatus]      = useState('all');

  const newManualForm = () => ({ client_name: '', client_phone: '', client_email: '', booking_date: today(), booking_time: '', total_amount: '', deposit_amount: '0', total_duration: '60', status: 'confirmed' });
  const [manualForm,  setManualForm]  = useState(newManualForm());
  const [savingManual,setSavingManual] = useState(false);

  const fetchBookings = useCallback(async () => {
    const data = await apiFetchBookings(200);
    setBks(data); setExistingBookings(data);
  }, []);

  const loadPhotosForBooking = async (bookingId) => {
    if (photosByBooking[bookingId]) return;
    const photos = await fetchBookingPhotos(bookingId);
    setPhotosByBooking(prev => ({ ...prev, [bookingId]: photos }));
  };

  useEffect(() => {
    if (!('Notification' in window)) return;
    setNotifStatus(Notification.permission);
    if (Notification.permission === 'default') {
      Notification.requestPermission().then(perm => {
        setNotifStatus(perm);
        if (perm === 'granted' && 'serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then(reg => {
            if ('periodicSync' in reg) reg.periodicSync.register('check-appointments', { minInterval: 30 * 60 * 1000 }).catch(() => {});
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    fetchShopDataForBookings().then(({ shopHours: h, holidays: hol, lunchBreak: lb }) => { setShopHours(h); setHolidays(hol); setLunchBreak(lb); });
    fetchSubServices().then(setAllServices);
    fetchCategories().then(setAllCategories);
  }, [fetchBookings]);

  useBookingsRealtime(fetchBookings);

  useEffect(() => {
    const fireNotification = (title, body, tag) => {
      if ('serviceWorker' in navigator && Notification.permission === 'granted') {
        navigator.serviceWorker.ready.then(reg => {
          reg.showNotification(title, { body, icon: '/logo192.png', badge: '/logo192.png', vibrate: [300,100,300,100,300], tag });
        });
      }
    };
    const interval = setInterval(() => {
      const now = new Date();
      bks.forEach(b => {
        if (b.status === 'cancelled' || b.status === 'completed') return;
        const apptTime = new Date(b.booking_date);
        const diffMins = (apptTime - now) / 60000;
        if (diffMins > 0 && diffMins <= 31 && !alertFired.has(b.id)) {
          fireNotification('⏰ Appointment in 30 minutes!', `${b.client_name} · ${b.service_name || 'Nail Service'} at ${apptTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`, `appt-${b.id}`);
          setAlertFired(prev => new Set([...prev, b.id]));
        }
      });
    }, 60000);
    return () => clearInterval(interval);
  }, [bks, alertFired]);

  const availableSlotsForEdit   = useMemo(() => computeSlots({ date: editForm.booking_date,   shopHours, holidays, existingBookings, excludeBookingId: editingBooking?.id, durationMins: editingBooking?.total_duration || 60, lunchBreak }), [editForm.booking_date,   editingBooking, shopHours, holidays, existingBookings, lunchBreak]);
  const availableSlotsForManual = useMemo(() => computeSlots({ date: manualForm.booking_date, shopHours, holidays, existingBookings, durationMins: parseInt(manualForm.total_duration) || 60, lunchBreak }),                                     [manualForm.booking_date, manualForm.total_duration, shopHours, holidays, existingBookings, lunchBreak]);

  const sendConfirmEmail = async (booking) => {
    if (!booking.client_email) throw new Error('No client email.');
    await sendEmail('booking_confirmation', booking);
  };

  const cancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking? The deposit will be automatically refunded if one was charged.')) return;
    const booking = bks.find(b => b.id === id);
    try { await updateBooking(id, { status: 'cancelled', cancelled_at: new Date().toISOString() }); } catch (err) { alert('Error cancelling: ' + err.message); return; }
    if (booking?.calendar_event_id) invokeCalendar('delete', { calendar_event_id: booking.calendar_event_id }, id);
    const refundEligible = !!booking?.stripe_payment_intent_id;
    if (refundEligible) {
      try { await processRefund(booking.stripe_payment_intent_id); alert('Booking cancelled and deposit refunded.'); }
      catch (err) { await logError(supabase, 'stripe_refund', `Refund failed: ${err.message}`, { booking_id: id }); alert('Booking cancelled. Refund failed — please process manually: ' + err.message); }
    } else { alert('Booking cancelled (no deposit on record).'); }
    if (booking?.client_email) {
      const cancelPayload = { id: booking.id, client_name: booking.client_name, client_email: booking.client_email, service_name: booking.service_name, booking_date: booking.booking_date, deposit_amount: booking.deposit_amount, refund_eligible: refundEligible, refund_amount: refundEligible ? booking.deposit_amount : 0 };
      sendEmail('cancellation_confirmation', cancelPayload);
      sendEmail('admin_cancellation_notification', cancelPayload);
    }
    fetchBookings();
  };

  const handleManualRefund = async (booking) => {
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) return alert('환불 금액을 올바르게 입력해주세요.');
    if (!window.confirm(`${booking.client_name}에게 $${amount} 환불을 처리할까요?`)) return;
    setRefundLoading(true);
    try { await processRefund(booking.stripe_payment_intent_id, amount); alert(`$${amount} 환불이 성공적으로 처리됐어요!`); setRefundingId(null); fetchBookings(); }
    catch (err) { await logError(supabase, 'stripe_refund', `Manual refund failed: ${err.message}`, { booking_id: booking.id }); alert('환불 실패: ' + err.message); }
    setRefundLoading(false);
  };

  const handleNoShow = async (booking) => {
    if (!window.confirm(`Mark ${booking.client_name}'s booking as No Show?`)) return;
    setSendingId(booking.id);
    try { await updateBooking(booking.id, { status: 'no_show' }); await incrementClientNoShow(booking.client_name, booking.client_phone); }
    catch (err) { alert('Error: ' + err.message); }
    setSendingId(null); fetchBookings();
  };

  const manualSelectedServices = useMemo(() => {
    const result = [];
    allCategories.forEach(cat => {
      const sel = manualSelections[cat.id];
      if (!sel) return;
      if (cat.selection_type === 'single') {
        const svc = allServices.find(s => s.id === sel);
        if (svc) result.push(svc);
      } else {
        (Array.isArray(sel) ? sel : []).forEach(id => {
          const svc = allServices.find(s => s.id === id);
          if (svc) result.push(svc);
        });
      }
    });
    return result;
  }, [allCategories, allServices, manualSelections]);

  const manualServiceName  = manualSelectedServices.map(s => s.name).filter(Boolean).join(' · ');
  const manualAutoTotal    = manualSelectedServices.reduce((sum, s) => sum + Number(s.price    || 0), 0);
  const manualAutoDuration = manualSelectedServices.reduce((sum, s) => sum + Number(s.duration || 0), 0) || 60;

  const handleManualToggle = (cat, svcId) => {
    if (cat.selection_type === 'single') {
      setManualSelections(prev => ({ ...prev, [cat.id]: prev[cat.id] === svcId ? null : svcId }));
    } else {
      setManualSelections(prev => {
        const cur  = Array.isArray(prev[cat.id]) ? prev[cat.id] : [];
        const next = cur.includes(svcId) ? cur.filter(id => id !== svcId) : [...cur, svcId];
        return { ...prev, [cat.id]: next };
      });
    }
    setManualForm(f => ({ ...f, booking_time: '' }));
  };

  const saveManualBooking = async () => {
    if (!manualForm.client_name.trim()) return alert('Client name is required.');
    if (!manualForm.booking_date)       return alert('Please select a date.');
    if (!manualForm.booking_time)       return alert('Please select a time slot.');
    const bookingTs = new Date(`${manualForm.booking_date}T${manualForm.booking_time}:00`).toISOString();
    const total   = parseFloat(manualForm.total_amount)   || manualAutoTotal;
    const deposit = parseFloat(manualForm.deposit_amount) || 0;
    const payload = { client_name: manualForm.client_name.trim(), client_phone: manualForm.client_phone.trim(), client_email: manualForm.client_email.trim() || null, service_name: manualServiceName || '—', booking_date: bookingTs, total_amount: total, deposit_amount: deposit, balance_due: Math.max(0, total - deposit), status: manualForm.status, has_allergy: false, total_duration: parseInt(manualForm.total_duration) || manualAutoDuration };
    setSavingManual(true);
    try {
      const inserted = await insertManualBooking(payload);
      upsertClientFromBooking({ name: manualForm.client_name.trim(), phone: manualForm.client_phone.trim(), email: manualForm.client_email.trim() || null });
      invokeCalendar('create', { booking: inserted }, inserted.id)
        .then(calData => { if (calData?.event_id) setCalendarEventId(inserted.id, calData.event_id); });
      if (inserted.client_email) {
        const emailType = manualForm.status === 'confirmed' ? 'booking_confirmation' : 'booking_pending';
        sendEmail(emailType, { ...inserted, booking_reference: inserted.booking_reference });
      }
    } catch (err) { setSavingManual(false); return alert('Could not add booking: ' + err.message); }
    setSavingManual(false); setAddingManual(false); setManualForm(newManualForm()); setManualSelections({}); fetchBookings();
  };

  const handleDeleteBooking = async (id, status) => {
    if (status === 'no_show') {
      if (!window.confirm('Remove this no-show from the dashboard? The record will be kept in the client\'s history.')) return;
      try { await apiArchiveBooking(id); fetchBookings(); } catch (err) { alert('Could not archive: ' + err.message); }
    } else {
      if (!window.confirm('Permanently delete this booking record? This cannot be undone.')) return;
      try { await apiDeleteBooking(id); fetchBookings(); } catch (err) { alert('Could not delete: ' + err.message); }
    }
  };

  const startEdit = (booking) => {
    const d = new Date(booking.booking_date);
    setEditForm({
      booking_date:   d.toISOString().slice(0, 10),
      booking_time:   d.toTimeString().slice(0, 5),
      service_name:   booking.service_name  || '',
      client_name:    booking.client_name   || '',
      client_phone:   booking.client_phone  || '',
      client_email:   booking.client_email  || '',
      total_amount:   String(booking.total_amount   ?? ''),
      deposit_amount: String(booking.deposit_amount ?? ''),
      status:         booking.status        || 'confirmed',
    });
    setEditingBooking(booking);
  };

  const saveEdit = async () => {
    if (!editForm.booking_date || !editForm.booking_time) return alert('Please select date and time.');
    const newTs   = new Date(`${editForm.booking_date}T${editForm.booking_time}:00`).toISOString();
    const total   = parseFloat(editForm.total_amount)   || 0;
    const deposit = parseFloat(editForm.deposit_amount) || 0;
    try {
      await updateBooking(editingBooking.id, {
        booking_date:   newTs,
        service_name:   editForm.service_name,
        client_name:    editForm.client_name.trim(),
        client_phone:   editForm.client_phone.trim(),
        client_email:   editForm.client_email.trim() || null,
        total_amount:   total,
        deposit_amount: deposit,
        balance_due:    Math.max(0, total - deposit),
        status:         editForm.status,
      });
      const emailAddr = editForm.client_email.trim() || editingBooking.client_email;
      const updated   = { ...editingBooking, booking_date: newTs, service_name: editForm.service_name, client_name: editForm.client_name, client_email: emailAddr, status: editForm.status };
      const dateChanged              = newTs !== editingBooking.booking_date;
      const statusChangedToConfirmed = editForm.status === 'confirmed' && editingBooking.status !== 'confirmed';
      if (dateChanged && emailAddr) {
        sendEmail('reschedule_confirmation',      { ...updated, old_booking_date: editingBooking.booking_date });
        sendEmail('admin_reschedule_notification', { ...updated, old_booking_date: editingBooking.booking_date });
      }
      if (statusChangedToConfirmed) {
        if (emailAddr) { try { await sendConfirmEmail({ ...editingBooking, client_email: emailAddr }); } catch (err) { await logError(supabase, 'email', `Confirm email failed: ${err.message}`, { booking_id: editingBooking.id }); } }
        invokeCalendar('update', { booking: { ...updated, status: 'confirmed' }, calendar_event_id: editingBooking.calendar_event_id || null }, editingBooking.id);
      } else if (dateChanged) {
        invokeCalendar('update', { booking: updated, calendar_event_id: editingBooking.calendar_event_id }, editingBooking.id);
      }
      alert('Booking updated!'); setEditingBooking(null); fetchBookings();
    } catch (err) { alert('Could not update booking: ' + err.message); }
  };

  const handleComplete = async () => {
    const details = {};
    if (parseFloat(paymentDetails.card)     > 0) details.card     = parseFloat(paymentDetails.card);
    if (parseFloat(paymentDetails.cash)     > 0) details.cash     = parseFloat(paymentDetails.cash);
    if (parseFloat(paymentDetails.transfer) > 0) details.transfer = parseFloat(paymentDetails.transfer);
    setSendingId(completingBooking.id);
    try {
      await updateBooking(completingBooking.id, {
        status: 'completed',
        ...(Object.keys(details).length > 0 && { payment_details: details }),
      });
    } catch (err) { alert('Could not complete booking: ' + err.message); }
    setSendingId(null);
    setCompletingBooking(null);
    setPaymentDetails({ card: '', cash: '', transfer: '' });
    fetchBookings();
  };

  const statusBorderColor  = (s) => ({ confirmed: P.green, completed: P.deep, cancelled: P.red, no_show: '#8B3A3A', pending: P.amber }[s] || P.amber);
  const currentPhotos      = viewingPhotos ? (photosByBooking[viewingPhotos] || []) : [];
  const activeBks          = bks.filter(b => b.status !== 'blocked');
  const displayedBks       = filterStatus === 'all' ? activeBks : activeBks.filter(b => b.status === filterStatus);
  const paymentEnteredTotal = (parseFloat(paymentDetails.card) || 0) + (parseFloat(paymentDetails.cash) || 0) + (parseFloat(paymentDetails.transfer) || 0);

  if (addingManual) return (
    <div>
      <Btn variant="ghost" size="sm" onClick={() => { setAddingManual(false); setManualForm(newManualForm()); setManualSelections({}); }}>← Back</Btn>
      <div style={{ ...cardStyle, marginTop: '14px', border: `2px solid ${P.rose}88` }}>
        <h3 style={{ color: P.deep, marginBottom: '6px' }}>📋 Manual Booking</h3>
        <p style={{ fontSize: '13px', color: P.muted, marginBottom: '16px' }}>전화 / 인스타그램 등으로 예약된 고객을 직접 추가합니다.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div><label style={labelStyle}>Client Name *</label><input style={inputStyle} placeholder="Full name" value={manualForm.client_name} onChange={e => setManualForm(f => ({ ...f, client_name: e.target.value }))} /></div>
          <div><label style={labelStyle}>Phone</label><input style={inputStyle} placeholder="04XX XXX XXX" value={manualForm.client_phone} onChange={e => setManualForm(f => ({ ...f, client_phone: e.target.value }))} /></div>
        </div>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Email (optional)</label><input style={inputStyle} placeholder="client@email.com" value={manualForm.client_email} onChange={e => setManualForm(f => ({ ...f, client_email: e.target.value }))} /></div>
        {allCategories.filter(cat => allServices.some(s => s.category_id === cat.id)).map(cat => {
          const catSvcs = allServices.filter(s => s.category_id === cat.id).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
          const isSingle = cat.selection_type === 'single';
          const catSel   = manualSelections[cat.id];
          return (
            <div key={cat.id} style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{cat.name} <span style={{ fontWeight: 400, textTransform: 'none' }}>({isSingle ? 'choose one' : 'multi'})</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {catSvcs.map(svc => {
                  const isSelected = isSingle ? catSel === svc.id : Array.isArray(catSel) && catSel.includes(svc.id);
                  return (
                    <button key={svc.id} type="button" onClick={() => handleManualToggle(cat, svc.id)}
                      style={{ padding: '7px 12px', borderRadius: '8px', border: `1.5px solid ${isSelected ? P.rose : P.border}`, background: isSelected ? P.rose + '15' : P.white, color: isSelected ? P.rose : P.text, fontSize: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.12s' }}>
                      {isSelected ? '✓ ' : ''}{svc.name}{Number(svc.price) > 0 ? ` · $${svc.price}` : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {manualSelectedServices.length > 0 && (
          <div style={{ background: P.peach + '30', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: P.deep }}>
            <strong>{manualServiceName}</strong>
            <span style={{ marginLeft: '10px', color: P.rose, fontWeight: 700 }}>${manualAutoTotal}</span>
            <span style={{ marginLeft: '8px', color: P.muted }}>{manualAutoDuration} min</span>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div><label style={labelStyle}>Total ($) <span style={{ fontWeight: 400 }}>auto</span></label><input type="number" min="0" step="0.01" style={inputStyle} placeholder={String(manualAutoTotal || 0)} value={manualForm.total_amount} onChange={e => setManualForm(f => ({ ...f, total_amount: e.target.value }))} /></div>
          <div><label style={labelStyle}>Deposit Paid ($)</label><input type="number" min="0" step="0.01" style={inputStyle} placeholder="0" value={manualForm.deposit_amount} onChange={e => setManualForm(f => ({ ...f, deposit_amount: e.target.value }))} /></div>
          <div><label style={labelStyle}>Duration (mins) <span style={{ fontWeight: 400 }}>auto</span></label><input type="number" min="15" step="15" style={inputStyle} placeholder={String(manualAutoDuration)} value={manualForm.total_duration} onChange={e => setManualForm(f => ({ ...f, total_duration: e.target.value, booking_time: '' }))} /></div>
        </div>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Date *</label><input type="date" style={inputStyle} value={manualForm.booking_date} onChange={e => setManualForm(f => ({ ...f, booking_date: e.target.value, booking_time: '' }))} /></div>
        {manualForm.booking_date && (
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Time *</label>
            {availableSlotsForManual.length === 0 ? <p style={{ color: P.muted, fontSize: '13px' }}>No available slots on this date.</p> : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableSlotsForManual.map(t => <button key={t} onClick={() => setManualForm(f => ({ ...f, booking_time: t }))} style={{ padding: '8px 14px', borderRadius: '8px', border: `1.5px solid ${manualForm.booking_time === t ? P.rose : P.border}`, background: manualForm.booking_time === t ? P.rose : P.white, color: manualForm.booking_time === t ? P.white : P.text, fontWeight: 600, cursor: 'pointer' }}>{t}</button>)}
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Status</label>
          <select style={inputStyle} value={manualForm.status} onChange={e => setManualForm(f => ({ ...f, status: e.target.value }))}>
            <option value="confirmed">Confirmed</option><option value="pending">Pending</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={saveManualBooking} disabled={savingManual} style={{ flex: 1 }}>{savingManual ? 'Saving...' : 'Add Booking'}</Btn>
          <Btn variant="ghost" onClick={() => { setAddingManual(false); setManualForm(newManualForm()); setManualSelections({}); }}>Cancel</Btn>
        </div>
      </div>
    </div>
  );

  if (editingBooking) return (
    <div>
      <Btn variant="ghost" size="sm" onClick={() => setEditingBooking(null)}>← Back</Btn>
      <div style={{ ...cardStyle, marginTop: '14px' }}>
        <h3 style={{ color: P.deep, marginBottom: '16px' }}>✏️ Edit Booking</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div><label style={labelStyle}>Client Name *</label><input style={inputStyle} value={editForm.client_name} onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Full name" /></div>
          <div><label style={labelStyle}>Phone</label><input style={inputStyle} value={editForm.client_phone} onChange={e => setEditForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="04XX XXX XXX" /></div>
        </div>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Email</label><input style={inputStyle} value={editForm.client_email} onChange={e => setEditForm(f => ({ ...f, client_email: e.target.value }))} placeholder="client@email.com" /></div>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Service</label><input style={inputStyle} value={editForm.service_name} onChange={e => setEditForm(f => ({ ...f, service_name: e.target.value }))} placeholder="Service name" /></div>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={editForm.booking_date} onChange={e => setEditForm(f => ({ ...f, booking_date: e.target.value, booking_time: '' }))} /></div>
        {editForm.booking_date && (
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Time</label>
            {availableSlotsForEdit.length === 0
              ? <p style={{ color: P.muted, fontSize: '13px' }}>No available slots on this date.</p>
              : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availableSlotsForEdit.map(t => (
                    <button key={t} onClick={() => setEditForm(f => ({ ...f, booking_time: t }))}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: `1.5px solid ${editForm.booking_time === t ? P.rose : P.border}`, background: editForm.booking_time === t ? P.rose : P.white, color: editForm.booking_time === t ? P.white : P.text, fontWeight: 600, cursor: 'pointer' }}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div><label style={labelStyle}>Total Amount ($)</label><input type="number" min="0" step="0.01" style={inputStyle} value={editForm.total_amount} onChange={e => setEditForm(f => ({ ...f, total_amount: e.target.value }))} placeholder="0" /></div>
          <div><label style={labelStyle}>Deposit Paid ($)</label><input type="number" min="0" step="0.01" style={inputStyle} value={editForm.deposit_amount} onChange={e => setEditForm(f => ({ ...f, deposit_amount: e.target.value }))} placeholder="0" /></div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Status</label>
          <select style={inputStyle} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={saveEdit} style={{ flex: 1 }}>Save Changes</Btn>
          <Btn variant="ghost" onClick={() => setEditingBooking(null)}>Cancel</Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>Live Bookings Dashboard</h2>
        <Btn size="sm" onClick={() => setAddingManual(true)}>+ Add Booking</Btn>
      </div>

      {viewingPhotos && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <button onClick={() => setViewingPhotos(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', borderRadius: '50%', width: '40px', height: '40px' }}>×</button>
          <p style={{ color: '#fff', marginBottom: '16px', fontWeight: 700 }}>Client Reference Photos ({currentPhotos.length})</p>
          {currentPhotos.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>No photos uploaded for this booking.</p> : (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '600px' }}>
              {currentPhotos.map(p => <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"><img src={p.url} alt={p.file_name} style={{ width: '140px', height: '140px', objectFit: 'cover', borderRadius: '10px', border: '2px solid rgba(255,255,255,0.3)' }} /></a>)}
            </div>
          )}
        </div>
      )}

      {completingBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: P.white, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '400px', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <h3 style={{ color: P.deep, marginBottom: '4px', fontSize: '18px' }}>✅ Complete Booking</h3>
            <p style={{ fontSize: '13px', color: P.muted, marginBottom: '16px' }}>
              {completingBooking.client_name} · {completingBooking.service_name || 'Nail Service'}<br />
              {new Date(completingBooking.booking_date).toLocaleString('en-AU')}
            </p>
            <div style={{ background: P.peach + '30', borderRadius: '10px', padding: '10px 14px', marginBottom: '18px', fontSize: '13px' }}>
              <span style={{ color: P.muted }}>Balance due: </span>
              <strong style={{ color: P.rose, fontSize: '15px' }}>${Number(completingBooking.balance_due).toFixed(2)}</strong>
            </div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: P.deep, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Payment Method</p>
            {[
              { key: 'card',     label: '💳 Card' },
              { key: 'cash',     label: '💵 Cash' },
              { key: 'transfer', label: '🏦 Bank Transfer' },
            ].map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ width: '110px', fontSize: '13px', fontWeight: 600, color: P.text }}>{label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: P.muted }}>$</span>
                  <input type="number" min="0" step="0.01" style={{ ...inputStyle, padding: '7px 10px' }} placeholder="0"
                    value={paymentDetails[key]}
                    onChange={e => setPaymentDetails(prev => ({ ...prev, [key]: e.target.value }))} />
                </div>
              </div>
            ))}
            {paymentEnteredTotal > 0 && (
              <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: '10px', marginTop: '4px', marginBottom: '14px', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: P.muted }}>Total entered</span>
                <strong style={{ color: Math.abs(paymentEnteredTotal - Number(completingBooking.balance_due)) < 0.01 ? P.green : P.amber }}>
                  ${paymentEnteredTotal.toFixed(2)}
                  {Math.abs(paymentEnteredTotal - Number(completingBooking.balance_due)) >= 0.01 && ` (due $${Number(completingBooking.balance_due).toFixed(2)})`}
                </strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <Btn onClick={handleComplete} disabled={sendingId === completingBooking.id} style={{ flex: 1 }}>
                {sendingId === completingBooking.id ? 'Saving...' : 'Mark Complete'}
              </Btn>
              <Btn variant="ghost" onClick={() => { setCompletingBooking(null); setPaymentDetails({ card: '', cash: '', transfer: '' }); }}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {notifStatus === 'denied' && <div style={{ background: '#FFF0F0', border: `1px solid ${P.red}60`, borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: P.red }}>⚠️ Notifications blocked. Go to browser Settings → Notifications to enable 30-min reminders.</div>}
      {notifStatus === 'granted' && <div style={{ background: '#F0FFF5', border: `1px solid ${P.green}60`, borderRadius: '10px', padding: '8px 14px', marginBottom: '14px', fontSize: '12px', color: P.green }}>✓ Push notifications active — 30-min alerts enabled</div>}

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {['all','pending','confirmed','completed','no_show','cancelled'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            style={{ padding: '5px 12px', borderRadius: '20px', border: `1.5px solid ${filterStatus === s ? P.rose : P.border}`, background: filterStatus === s ? P.rose + '15' : 'transparent', color: filterStatus === s ? P.rose : P.muted, cursor: 'pointer', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
            {s === 'all' ? `All (${activeBks.length})` : `${s.replace('_',' ')} (${activeBks.filter(x => x.status === s).length})`}
          </button>
        ))}
      </div>

      {displayedBks.length === 0 ? (
        <p style={{ textAlign: 'center', color: P.muted, padding: '40px' }}>No bookings in this filter.</p>
      ) : displayedBks.map(b => (
        <div key={b.id} style={{ ...cardStyle, borderLeft: `5px solid ${statusBorderColor(b.status)}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontWeight: 700, color: P.deep }}>{new Date(b.booking_date).toLocaleString('en-AU')}</span>
              <h4 style={{ margin: '4px 0' }}>{b.client_name} ({b.client_phone})</h4>
              {b.service_name && <p style={{ fontSize: '13px', color: P.deep, fontWeight: 700, marginBottom: '2px' }}>{b.service_name}</p>}
              <p style={{ fontSize: '13px', color: P.muted }}>💰 Total: ${b.total_amount} | Paid: ${b.deposit_amount} | <strong style={{ color: P.rose }}>Due: ${b.balance_due}</strong></p>
              {b.payment_details && (
                <p style={{ fontSize: '12px', color: P.deep, marginTop: '2px' }}>
                  💳 {[
                    b.payment_details.card     && `Card $${b.payment_details.card}`,
                    b.payment_details.cash     && `Cash $${b.payment_details.cash}`,
                    b.payment_details.transfer && `Transfer $${b.payment_details.transfer}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
              {b.has_allergy === true  && <p style={{ fontSize: '12px', color: P.red,   fontWeight: 700, marginTop: '4px' }}>⚠️ Client reported allergy</p>}
              {b.has_allergy === false && <p style={{ fontSize: '12px', color: P.green, marginTop: '4px' }}>✓ No allergy</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
              <Badge color={{ confirmed: P.green, completed: P.deep, cancelled: P.red, no_show: '#B56060', pending: P.amber }[b.status] || P.amber}>{b.status.replace('_',' ').toUpperCase()}</Badge>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Btn size="sm" variant="ghost" onClick={() => { loadPhotosForBooking(b.id); setViewingPhotos(b.id); }}>📸</Btn>
                {b.status !== 'completed' && b.status !== 'cancelled' && b.status !== 'no_show' && (
                  <>
                    <Btn size="sm" variant="ghost" onClick={() => startEdit(b)}>✏️ Edit</Btn>
                    {b.status === 'confirmed' && (
                      <Btn size="sm" variant="secondary" disabled={sendingId === b.id}
                        onClick={() => { setCompletingBooking(b); setPaymentDetails({ card: '', cash: '', transfer: '' }); }}>
                        Complete
                      </Btn>
                    )}
                  </>
                )}
                {b.status === 'confirmed' && (
                  <Btn size="sm" variant="ghost" disabled={sendingId === b.id} onClick={async () => { setSendingId(b.id); try { await sendConfirmEmail(b); alert('Confirmation email re-sent!'); } catch (err) { await logError(supabase, 'email', `Re-send confirmation failed: ${err.message}`, { booking_id: b.id }); alert('Email failed: ' + err.message); } setSendingId(null); }}>📧 Re-send</Btn>
                )}
                {b.status !== 'cancelled' && b.status !== 'completed' && b.status !== 'no_show' && Number(b.balance_due) > 0 && (
                  <Btn size="sm" variant="ghost" style={{ color: P.green, borderColor: `${P.green}60`, fontWeight: 700 }} onClick={() => window.open('https://dashboard.stripe.com/payments', '_blank')}>💳 ${Number(b.balance_due).toFixed(2)} — Stripe</Btn>
                )}
                {b.stripe_payment_intent_id && Number(b.deposit_amount) > 0 && (
                  <Btn size="sm" variant="ghost" style={{ color: P.amber, borderColor: `${P.amber}60` }} onClick={() => { setRefundingId(refundingId === b.id ? null : b.id); setRefundAmount(String(b.deposit_amount)); }}>💸 Refund</Btn>
                )}
                {(b.status === 'pending' || b.status === 'confirmed') && <Btn size="sm" variant="ghost" style={{ color: '#8B3A3A', borderColor: '#DDB0B060' }} disabled={sendingId === b.id} onClick={() => handleNoShow(b)}>🚫 No Show</Btn>}
                {b.status !== 'cancelled' && b.status !== 'no_show' && <Btn size="sm" variant="danger" onClick={() => cancelBooking(b.id)}>× Cancel</Btn>}
                {!b.calendar_event_id && b.status !== 'cancelled' && b.status !== 'no_show' && (
                  <Btn size="sm" variant="ghost" style={{ color: P.muted, borderColor: `${P.border}`, fontSize: '10px' }}
                    disabled={sendingId === b.id}
                    onClick={async () => {
                      setSendingId(b.id);
                      await invokeCalendar('create', { booking: b }, b.id);
                      fetchBookings();
                      setSendingId(null);
                    }}>
                    📅 Sync Cal
                  </Btn>
                )}
                {(b.status === 'cancelled' || b.status === 'no_show') && <Btn size="sm" variant="danger" onClick={() => handleDeleteBooking(b.id, b.status)}>🗑️ Delete</Btn>}
              </div>
            </div>
          </div>
          {refundingId === b.id && (
            <div style={{ marginTop: '12px', borderTop: `1px solid ${P.border}`, paddingTop: '12px' }}>
              <p style={{ fontSize: '13px', fontWeight: 700, color: P.amber, marginBottom: '6px' }}>💸 Manual Refund</p>
              <p style={{ fontSize: '12px', color: P.muted, marginBottom: '10px' }}>결제된 예약금: <strong>${b.deposit_amount}</strong></p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: P.text }}>$</span>
                  <input type="number" min="0.01" step="0.01" style={{ ...inputStyle, padding: '7px 10px' }} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder={String(b.deposit_amount)} />
                </div>
                <Btn size="sm" variant="secondary" disabled={refundLoading} onClick={() => handleManualRefund(b)}>{refundLoading ? 'Processing...' : 'Process Refund'}</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setRefundingId(null)}>Cancel</Btn>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
